import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import Navbar from "src/components/Navbar";
import Footer from "src/components/Footer";
import { attemptTokenKey, fetchReport, type ReportPayload } from "src/services/diagnosticService";

const STATUS_LABELS: Record<string, string> = {
  secure: "Secure",
  on_track: "On track",
  developing: "Developing",
  struggling: "Needs support",
};

export default function DiagnosticReport() {
  const { attemptId = "" } = useParams();
  const [params] = useSearchParams();
  const [report, setReport] = useState<ReportPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    const token = params.get("token") || sessionStorage.getItem(attemptTokenKey(attemptId)) || "";
    if (!token) {
      setError("Report not found or link expired.");
      return;
    }
    if (params.get("token")) sessionStorage.setItem(attemptTokenKey(attemptId), token);

    let cancelled = false;
    let timer = 0;
    let attempts = 0;

    const load = () => {
      fetchReport(attemptId, token)
        .then((payload) => {
          if (!cancelled) {
            setReport(payload);
            setPending(false);
          }
        })
        .catch((err: unknown) => {
          if (cancelled) return;
          const message = err instanceof Error ? err.message : "The report could not be loaded.";
          if (message.toLowerCase().includes("prepared") && attempts < 12) {
            attempts += 1;
            setPending(true);
            timer = window.setTimeout(load, 5000);
            return;
          }
          setError(message);
        });
    };

    load();
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [attemptId, params]);

  const overall = report?.summary.overall;
  const status = overall?.status ?? "";

  return (
    <div className="flex min-h-screen flex-col bg-white">
      <Navbar />
      <main className="flex-1 px-4 py-10 sm:px-6">
        <div className="mx-auto w-full max-w-3xl">
          {!report && !error && (
            <p className="flex items-center gap-2 text-sm text-gray-600">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              {pending ? "Your report is being prepared — please wait." : "Loading the report…"}
            </p>
          )}
          {error && <p className="text-sm text-red-700" role="alert">{error}</p>}
          {report && overall && (
            <article className="flex flex-col gap-4">
              <p className="text-sm text-gray-600">{report.quiz.subjectName}</p>
              <h1 className="font-roboto text-3xl font-bold text-[#3e1168]">{report.quiz.title}</h1>
              <p className="text-sm text-gray-600">Diagnostic Quiz Report</p>
              <p className="font-roboto text-5xl font-bold text-[#3e1168]">{overall.score_pct ?? 0}%</p>
              <p className="text-base text-[#3e1168]">
                {overall.correct ?? 0} of {overall.total ?? 0} correct
                {status ? ` · ${STATUS_LABELS[status] ?? status}` : ""}
              </p>
              <p className="text-sm text-gray-600">
                {overall.correct ?? 0} correct · {overall.incorrect ?? 0} incorrect · {overall.unanswered ?? 0} unanswered
              </p>
              {report.summary.narrative && <p className="max-w-prose text-base leading-relaxed text-[#241432]">{report.summary.narrative}</p>}
              <p className="text-sm text-gray-600">
                Topic bars, the evidence table, question review, the PDF download, and the demo booking button are the next part of this report.
              </p>
            </article>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
